import { config } from 'app/config'

type UrlConstructor = (url: string) => string
type GetUrlConstructorByPrefix = (urlPrefix: string | undefined) => UrlConstructor

export enum URLPrefix {
  Root = 'root',
  Base = 'base',
  Booking = 'booking',
  DemDet = 'dem-det',
  Documents = 'documents',
  UserProfileCore = 'userProfileCore',
  PartnerBookingCore = 'partnerBookingCore',
  FxCore = 'fxCore',
  FeedCoreV2 = 'feedCoreV2',
  Subscriptions = 'subscriptions',
  CargoTypeCore = 'cargoTypeCore',
  PublicApi = 'publicApi',
  QuotationCore = 'quotationCore',
  BillingCore = 'billingCore',
  CustomStatus = 'custom-status',
  CarbonEmissions = 'carbonEmissions',
  CustomerMasterData = 'customerMasterData',
}

const joinUrlPath = (...allUrls: string[]): string => {
  return allUrls.reduce((urls, url) => {
    if (!urls) {
      return url
    }

    const separator = url[0] !== '/' && urls[urls.length - 1] !== '/' ? '/' : ''

    return urls + separator + url
  }, '')
}

const getRootUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.SHIPPING_ENDPOINT, url)
}
export const getAPIBaseUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.SHIPPING_ENDPOINT, '/api', url)
}
const getBookingUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.BOOKING_ENDPOINT, url)
}
const getAPIUserProfileCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.USER_PROFILE_CORE_ENDPOINT, url)
}
const getAPIPartnerBookingCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.PARTNER_BOOKING_CORE_ENDPOINT, url)
}
const getAPIFeedCoreV2Url: UrlConstructor = (url) => {
  return joinUrlPath(config.FEED_CORE_V2_ENDPOINT, url)
}
const getSubscriptionUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.SUBSCRIPTIONS_ENDPOINT, url)
}
const getDemDetUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.DEM_DET_CORE_ENDPOINT, url)
}
const getDocumentsCoreV2Url: UrlConstructor = (url) => {
  return joinUrlPath(config.DOCUMENTS_CORE_V2_ENDPOINT, url)
}
const getFxCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.FX_CORE_ENDPOINT, url)
}
const getCargoTypeCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.TRANSPORT_UNITS_ENDPOINT, url)
}
const getPublicApiCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.PUBLIC_API_ENDPOINT, url)
}
const getQuotationCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.QUOTATION_CORE_ENDPOINT, url)
}
const getBillingCoreUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.BILLING_CORE_ENDPOINT, url)
}

const getCustomStatusUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.CUSTOM_STATUS_ENDPOINT, url)
}

const getMasterDataUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.CUSTOMER_MASTER_DATA_ENDPOINT, url)
}

const getCarbonEmissionsUrl: UrlConstructor = (url) => {
  return joinUrlPath(config.CARBON_EMISSIONS_ENDPOINT, url)
}

const urlGetters: Record<URLPrefix, UrlConstructor> = {
  [URLPrefix.Root]: getRootUrl,
  [URLPrefix.Base]: getAPIBaseUrl,
  [URLPrefix.Booking]: getBookingUrl,
  [URLPrefix.DemDet]: getDemDetUrl,
  [URLPrefix.Documents]: getDocumentsCoreV2Url,
  [URLPrefix.UserProfileCore]: getAPIUserProfileCoreUrl,
  [URLPrefix.PartnerBookingCore]: getAPIPartnerBookingCoreUrl,
  [URLPrefix.FxCore]: getFxCoreUrl,
  [URLPrefix.FeedCoreV2]: getAPIFeedCoreV2Url,
  [URLPrefix.Subscriptions]: getSubscriptionUrl,
  [URLPrefix.CargoTypeCore]: getCargoTypeCoreUrl,
  [URLPrefix.PublicApi]: getPublicApiCoreUrl,
  [URLPrefix.QuotationCore]: getQuotationCoreUrl,
  [URLPrefix.BillingCore]: getBillingCoreUrl,
  [URLPrefix.CustomStatus]: getCustomStatusUrl,
  [URLPrefix.CarbonEmissions]: getCarbonEmissionsUrl,
  [URLPrefix.CustomerMasterData]: getMasterDataUrl,
}

export const getUrlConstructorByPrefix: GetUrlConstructorByPrefix = (urlPrefix = URLPrefix.Base) =>
  urlGetters[urlPrefix] ?? urlGetters[URLPrefix.Base]
